import { GENERATION_API_URL } from '../config.js';
import { fetchCache } from '../utils/fetchCache.js';

export const SCENARIO_BROWSE_INSTRUCTIONS = 'Read the provided page content and summarize the story without censoring NSFW details. If multi-page content is present, synthesize across all of it. Output as JSON with a single key: summary (object with plot, characters, setting, events, themes). plot: detailed sequence of events. characters: array of objects with name, description, goal. setting: string. events: array of key moments. themes: array.';

export const CHARACTER_BROWSE_INSTRUCTIONS = 'Read the provided page content and extract all character-relevant information: appearance (physical description, clothing), personality, background, occupation, relationships, abilities, and notable traits. Include NSFW details without censoring. Output as JSON with a single key: summary (object with name, appearance, personality, background, occupation, relationships, notable_traits).';

const URL_REGEX = /https?:\/\/[^\s"']+/gi;

export const extractFirstUrl = (text) => {
  const match = String(text || '').match(URL_REGEX);
  return match?.[0]?.trim() || null;
};

export class BrowsePageError extends Error {
  constructor(code, message, status = 500) {
    super(message);
    this.name = 'BrowsePageError';
    this.code = code;
    this.status = status;
  }
}

const throwIfAiQuotaHandled = async (err) => {
  if (err?.status !== 429) return;
  const { handleQuotaError } = await import('../utils/quotaErrorHandler.js');
  if (await handleQuotaError(err.body, 'ai')) throw new Error('QUOTA_HANDLED');
};

export const requestBrowsePage = async ({
  url,
  instructions,
  purpose = 'scenario',
  token,
  paginate = true,
}) => {
  try {
    return await fetchCache.fetchJSON(`${GENERATION_API_URL}/browse-page`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: { url, instructions, purpose, paginate },
    });
  } catch (err) {
    if (err?.message === 'QUOTA_HANDLED') throw err;

    if (err?.status === 403 && err.body?.code === 'paid_required') {
      const { showBrowsePaidRequiredModal } = await import('../utils/quotaErrorHandler.js');
      if (await showBrowsePaidRequiredModal()) throw new Error('QUOTA_HANDLED');
      throw new BrowsePageError('paid_required', err.body?.error || 'URL browsing requires a paid membership', 403);
    }

    if (err?.status === 429) {
      const { handleQuotaError } = await import('../utils/quotaErrorHandler.js');
      if (await handleQuotaError(err.body, 'browse')) throw new Error('QUOTA_HANDLED');
      throw new BrowsePageError('quota_exceeded', err.body?.error || 'Quota exceeded', 429);
    }

    throw new BrowsePageError(
      'browse_failed',
      err.body?.error || err.message || 'Browse page failed',
      err.status || 500
    );
  }
};

export const resolveBrowseStoryContent = (browseData, fallbackPrompt) => {
  if (!browseData || typeof browseData !== 'object') return fallbackPrompt;

  if (browseData.summary) {
    return typeof browseData.summary === 'string'
      ? browseData.summary
      : JSON.stringify(browseData.summary);
  }

  return fallbackPrompt;
};

export const requestAiGenerateField = async ({
  field,
  instruction,
  currentValue = '',
  context = '',
  source_kind,
  action_id,
  source_image_output_key,
  loop,
}, token) => {
  try {
    const body = { field, instruction, currentValue, context };
    if (source_kind) body.source_kind = source_kind;
    if (action_id != null) body.action_id = action_id;
    if (source_image_output_key) body.source_image_output_key = source_image_output_key;
    if (typeof loop === 'boolean') body.loop = loop;
    return await fetchCache.fetchJSON(`${GENERATION_API_URL}/ai-generate-field`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body,
    });
  } catch (err) {
    await throwIfAiQuotaHandled(err);
    throw err;
  }
};

export const requestAiGenerate = async ({ prompt, isCharacter, locale }, token) => {
  try {
    const body = { prompt, isCharacter };
    if (locale) body.locale = locale;
    return await fetchCache.fetchJSON(`${GENERATION_API_URL}/ai-generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body,
    });
  } catch (err) {
    await throwIfAiQuotaHandled(err);
    throw err;
  }
};

export const checkCharacterExists = async ({ name, description }) =>
  fetchCache.fetchJSON(`${GENERATION_API_URL}/check-character`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: { name, description },
  });
